import { z } from "zod";

/**
 * Tour Tabs — the agency-level configuration of the storefront tour-page tab
 * bar. Phase 1 contract (the foundation every layer derives from): the read DTO
 * for one tab plus the bulk "save the whole ordered list" write DTO that backs
 * the Configuration panel's single save bar.
 *
 * A tab's `slug` is its immutable key; `label` is the freely-editable display
 * name (rename "Maps" → "Route" without changing which section it points to).
 */

export const tourTabKindSchema = z.enum(["builtin", "custom"]);
export type TourTabKind = z.infer<typeof tourTabKindSchema>;

/** The built-in tabs every agency starts with. Stable, never deletable.
 *
 *  `dates` and `included` joined the list on 2026-09-22. They were the tour
 *  page's two "non-tab sections": traveler-web spliced them in relative to
 *  another tab (`dates` under Overview, `included` under Itinerary) because
 *  they had no tab of their own. That made them the only two sections an
 *  operator could not reorder or hide, and it let the two tab bars disagree —
 *  the desktop strip renders configured TABS and so omitted Dates, while the
 *  mobile bar renders rendered SECTIONS and so included it.
 *  The prototype has always drawn both as ordinary tabs.
 *
 *  What "rename" covers, precisely: `label` names the TAB, and every built-in
 *  now has one. For REVIEWS AND MAP it is also the section's heading on the
 *  page — `builtinTabLabel` (traveler-web) feeds it to those two sections, so
 *  renaming either tab renames the heading under it. Overview, Dates & prices,
 *  Itinerary, What's included and FAQs still render their own headings;
 *  renaming those tabs changes the tab only. Reviews and Map are the two
 *  #96/#116 asked for.
 *
 *  This paragraph said the opposite until 2026-10-02 ("It does NOT rewrite the
 *  section heading ... the two are allowed to differ"). Operators read the
 *  divergence as a bug, not as a feature: a tab saying "Reviews" over a heading
 *  saying "Reviews for this tour" looks like the rename half-applied. So the
 *  default built-in labels ARE now the headings — which is why they read "Map"
 *  and "Reviews" below rather than the longer in-page wording the components
 *  used to hardcode.
 *
 *  A CUSTOM tab still carries its own per-tour heading via
 *  `sectionNameOverride`, which outranks the label. */
export const builtinTourTabKeySchema = z.enum([
  "overview",
  "dates",
  "itinerary",
  "included",
  "maps",
  "reviews",
  "faqs",
]);
export type BuiltinTourTabKey = z.infer<typeof builtinTourTabKeySchema>;

/** Canonical seed list — the source of truth for AC-01 (auto-seeded defaults). */
export const DEFAULT_BUILTIN_TOUR_TABS: ReadonlyArray<{
  builtinKey: BuiltinTourTabKey;
  slug: string;
  label: string;
  sortOrder: number;
}> = [
  { builtinKey: "overview", slug: "overview", label: "Overview", sortOrder: 0 },
  {
    builtinKey: "dates",
    slug: "dates",
    label: "Dates & prices",
    sortOrder: 1,
  },
  {
    builtinKey: "itinerary",
    slug: "itinerary",
    label: "Itinerary",
    sortOrder: 2,
  },
  {
    builtinKey: "included",
    slug: "included",
    label: "What's included",
    sortOrder: 3,
  },
  { builtinKey: "maps", slug: "maps", label: "Map", sortOrder: 4 },
  { builtinKey: "reviews", slug: "reviews", label: "Reviews", sortOrder: 5 },
  { builtinKey: "faqs", slug: "faqs", label: "FAQs", sortOrder: 6 },
];

/** A label must be present and reasonably short. Backs AC-09 (empty → 422). */
const tourTabLabelSchema = z.string().trim().min(1).max(60);

/** Read DTO — one tab as returned by the storefront-config read path. */
export const tourTabDtoSchema = z.object({
  id: z.string().uuid(),
  agencyId: z.string().uuid(),
  slug: z.string().min(1),
  label: z.string().min(1),
  kind: tourTabKindSchema,
  /** Set only when `kind === "builtin"`; `null` for custom tabs. */
  builtinKey: builtinTourTabKeySchema.nullable(),
  sortOrder: z.number().int().min(0),
  visible: z.boolean(),
});
export type TourTabDto = z.infer<typeof tourTabDtoSchema>;

/**
 * One entry in a bulk save. Order is the array position (the server assigns
 * `sortOrder` from the index, mirroring the legacy drag-to-reorder save).
 * - `id` present  → an existing tab (built-in or custom) to update.
 * - `id` absent   → a new custom tab; the server generates its id + slug.
 * - `builtinKey` present → identifies a built-in tab (its `id` is also present).
 */
export const tourTabSaveItemSchema = z
  .object({
    id: z.string().uuid().nullish(),
    builtinKey: builtinTourTabKeySchema.nullish(),
    label: tourTabLabelSchema,
    visible: z.boolean(),
  })
  .strict();
export type TourTabSaveItem = z.infer<typeof tourTabSaveItemSchema>;

/**
 * Bulk save of the agency's whole tab list. A built-in tab cannot be deleted
 * (AC-07): the payload must contain every built-in key exactly once, so a
 * client that drops a built-in is rejected before anything is written.
 */
export const tourTabConfigSaveDtoSchema = z
  .object({
    tabs: z
      .array(tourTabSaveItemSchema)
      .min(builtinTourTabKeySchema.options.length)
      // every built-in + a generous custom-tab ceiling; guards unbounded growth
      // (each custom tab is a row + a content row per experience).
      .max(builtinTourTabKeySchema.options.length + 45),
  })
  .superRefine((value, ctx) => {
    const seen = new Map<BuiltinTourTabKey, number>();
    for (const tab of value.tabs) {
      if (tab.builtinKey) {
        seen.set(tab.builtinKey, (seen.get(tab.builtinKey) ?? 0) + 1);
      }
    }
    for (const key of builtinTourTabKeySchema.options) {
      const count = seen.get(key) ?? 0;
      if (count === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["tabs"],
          message: `Built-in tab "${key}" cannot be removed`,
        });
      } else if (count > 1) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["tabs"],
          message: `Built-in tab "${key}" appears more than once`,
        });
      }
    }
  });
export type TourTabConfigSaveDto = z.infer<typeof tourTabConfigSaveDtoSchema>;
